import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import { Search, Trash2, Plus, Minus, X, ScanLine, DollarSign, CreditCard, Smartphone, Clock, Printer, Receipt, ShoppingCart, Star, FileText, Lock, LockOpen, Users, Gift, Truck } from "lucide-react";
import { toast } from "sonner";
import { SaleReceiptContent, printReceiptNode } from "./factory/SaleReceipt";
import DeliveryFormFields from "./factory/DeliveryFormFields";
import { todayBR, addDays, fmtYmd } from "./factory/deliveryUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRICE_TYPES = [
  { key: "varejo", label: "Varejo" },
  { key: "atacado", label: "Atacado" },
  { key: "especial", label: "Especial" },
];

const fmt = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function FactoryPOS({ open, onClose, companyId, companyName, products, employees, clients, clientsCredit, bankAccounts = [], onCreated }) {
  const [priceType, setPriceType] = useState("varejo");
  const [sellerId, setSellerId] = useState("");
  const [sellerManual, setSellerManual] = useState("");
  const [search, setSearch] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [cart, setCart] = useState([]); // {product_id, name, unit, unit_price, quantity, max}
  const [discount, setDiscount] = useState("");
  const [freight, setFreight] = useState("");
  // Entrega
  const [withDelivery, setWithDelivery] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [deliveryDlg, setDeliveryDlg] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("dinheiro");
  const [amountReceived, setAmountReceived] = useState("");
  const [cardInstallments, setCardInstallments] = useState(1);
  const [bankAccountId, setBankAccountId] = useState(""); // conta que recebe (PIX/débito/crédito)
  const [pixReceived, setPixReceived] = useState("");    // valor recebido via PIX (pode ser > total)
  const [pixAttachmentUrl, setPixAttachmentUrl] = useState("");
  const [pixAttachmentName, setPixAttachmentName] = useState("");
  const [uploadingPix, setUploadingPix] = useState(false);
  const [lastBankByMethod, setLastBankByMethod] = useState({}); // { pix: id, cartao_debito: id, ... }
  const pixFileRef = useRef(null);
  const [clientId, setClientId] = useState("");
  const [clientNameManual, setClientNameManual] = useState("");
  const [aprazoCount, setAprazoCount] = useState(1);
  const [isPixAprazo, setIsPixAprazo] = useState(false);
  const [firstDue, setFirstDue] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null); // sale data after confirm
  const [qtyDlg, setQtyDlg] = useState({ open: false, product: null, qty: "1" });
  const searchRef = useRef(null);
  const barcodeRef = useRef(null);
  const receiptRef = useRef(null);
  const qtyInputRef = useRef(null);
  // Cash session
  const [cashSession, setCashSession] = useState(null);
  const [openSessionDlg, setOpenSessionDlg] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [openingBankId, setOpeningBankId] = useState("");
  // Favorites
  const [favorites, setFavorites] = useState([]);
  // NFC-e
  const [emitNfce, setEmitNfce] = useState(false);
  const [nfceCpfCnpj, setNfceCpfCnpj] = useState("");
  const [lastSaleId, setLastSaleId] = useState(null);
  // Cliente dialog
  const [clientDlg, setClientDlg] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  // Combos
  const [combos, setCombos] = useState([]);
  const [leftView, setLeftView] = useState("produtos");

  // ---- Load cash session + favorites on open ----
  const loadSession = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/cash-sessions/current`);
      setCashSession(data || null);
      if (!data) setOpenSessionDlg(true);
    } catch (e) {
      setCashSession(null);
    }
  };
  const loadFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/favorite-clients`);
      setFavorites(data || []);
    } catch (e) { /* ignore */ }
  };
  const loadCombos = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/combos?active_only=true`);
      setCombos(data || []);
    } catch (e) { setCombos([]); }
  };
  const loadUserPrefs = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/${companyId}/factory/user-preferences`);
      const prefs = {};
      ["pix", "cartao_debito", "cartao_credito"].forEach(m => {
        const key = `last_bank_account_${m}`;
        if (data?.[key]) prefs[m] = data[key];
      });
      setLastBankByMethod(prefs);
    } catch (e) { /* ignore */ }
  };

  // ---- Reset on open ----
  useEffect(() => {
    if (open) {
      setPriceType("varejo");
      setSellerId("");
      setSellerManual("");
      setSearch("");
      setBarcodeInput("");
      setCart([]);
      setDiscount("");
      setFreight("");
      setWithDelivery(false);
      setDelivery(null);
      axios.get(`${API}/companies/${companyId}/factory/delivery-drivers`).then(r => setDrivers(r.data || [])).catch(() => setDrivers([]));
      setPaymentOpen(false);
      setPayMethod("dinheiro");
      setAmountReceived("");
      setCardInstallments(1);
      setPixReceived("");
      setPixAttachmentUrl("");
      setPixAttachmentName("");
      // Default bank: a primeira conta "default" ou a primeira da lista
      const defBank = (bankAccounts || []).find(b => b.is_default) || (bankAccounts || [])[0];
      setBankAccountId(defBank?.id || "");
      setClientId("");
      setClientNameManual("");
      setAprazoCount(1);
      setIsPixAprazo(false);
      setReceipt(null);
      setEmitNfce(false);
      setNfceCpfCnpj("");
      setLastSaleId(null);
      loadSession();
      loadFavorites();
      loadUserPrefs();
      loadCombos();
      setLeftView("produtos");
      setTimeout(() => searchRef.current?.focus(), 150);
    }
  }, [open]);

  // Troca a conta pré-selecionada quando o operador muda o método (usa última conta usada por método)
  useEffect(() => {
    if (!["pix", "cartao_debito", "cartao_credito"].includes(payMethod)) return;
    const last = lastBankByMethod[payMethod];
    if (last && (bankAccounts || []).some(b => b.id === last)) {
      setBankAccountId(last);
    }
  }, [payMethod, lastBankByMethod]);

  // ---- Preço do produto conforme price_type ----
  const priceOf = (prod) => {
    if (priceType === "atacado") return Number(prod.price_wholesale || 0);
    if (priceType === "especial") return Number(prod.price_special || 0);
    return Number(prod.price_retail || 0);
  };

  const availableProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (products || []).filter(p => {
      if (!q) return true;
      return (p.name || "").toLowerCase().includes(q)
        || (p.barcode || "").toLowerCase().includes(q)
        || (p.code || "").toLowerCase() === q
        || (p.code || "").toLowerCase().replace(/^0+/, "") === q.replace(/^0+/, "");
    });
  }, [products, search]);

  const addProduct = (prod, qty = 1) => {
    if (!prod) return;
    const price = priceOf(prod);
    if (price <= 0) { toast.error(`Produto ${prod.name} não tem preço ${priceType} definido`); return; }
    if ((prod.quantity || 0) <= 0) { toast.error(`${prod.name} sem estoque`); return; }
    setCart(prev => {
      const i = prev.findIndex(c => c.product_id === prod.id);
      if (i >= 0) {
        const existing = prev[i];
        const newQty = existing.quantity + qty;
        if (newQty > prod.quantity) { toast.warning(`Estoque limitado: ${prod.quantity}`); return prev; }
        const copy = [...prev]; copy[i] = { ...existing, quantity: newQty }; return copy;
      }
      if (qty > prod.quantity) { toast.warning(`Estoque limitado: ${prod.quantity}`); return prev; }
      return [...prev, {
        product_id: prod.id, name: prod.name, unit: prod.unit || "un",
        unit_price: price, quantity: qty, max: prod.quantity,
      }];
    });
  };

  // ---- Combos: distribui o desconto proporcionalmente entre os produtos ----
  const comboPricing = (combo) => {
    const comps = (combo.items || []).map(it => {
      const p = (products || []).find(x => x.id === it.product_id);
      return p ? { prod: p, qty: Number(it.quantity), price: priceOf(p) } : null;
    });
    if (comps.length === 0 || comps.some(c => !c || c.price <= 0)) return null;
    const base = comps.reduce((s, c) => s + c.qty * c.price, 0);
    const final = combo.discount_type === "percent" ? base * (1 - Number(combo.discount_value) / 100) : Number(combo.discount_value);
    const factor = base > 0 ? final / base : 0;
    const components = comps.map(c => ({ product_id: c.prod.id, name: c.prod.name, unit: c.prod.unit || "un", qty: c.qty, unit_price: Math.round(c.price * factor * 100) / 100 }));
    const diff = Math.round((final - components.reduce((s, c) => s + c.qty * c.unit_price, 0)) * 100) / 100;
    if (diff !== 0) {
      const t = components.reduce((a, b) => (b.qty < a.qty ? b : a));
      t.unit_price = Math.round((t.unit_price + diff / t.qty) * 100) / 100;
    }
    const unit_price = Math.round(components.reduce((s, c) => s + c.qty * c.unit_price, 0) * 100) / 100;
    const max = Math.min(...comps.map(c => Math.floor(Number(c.prod.quantity || 0) / c.qty)));
    return { base, unit_price, components, max };
  };

  const addCombo = (combo) => {
    const pr = comboPricing(combo);
    if (!pr) { toast.error(`Combo ${combo.name}: algum produto sem preço ${priceType}`); return; }
    if (pr.max <= 0) { toast.error(`Combo ${combo.name} sem estoque suficiente`); return; }
    setCart(prev => {
      const key = `combo:${combo.id}`;
      const i = prev.findIndex(c => c.product_id === key);
      if (i >= 0) {
        if (prev[i].quantity + 1 > pr.max) { toast.warning(`Estoque limitado: ${pr.max} combo(s)`); return prev; }
        const copy = [...prev]; copy[i] = { ...copy[i], quantity: copy[i].quantity + 1 }; return copy;
      }
      return [...prev, { product_id: key, is_combo: true, combo_id: combo.id, name: combo.name, unit: "combo", unit_price: pr.unit_price, quantity: 1, max: pr.max, components: pr.components }];
    });
    toast.success(`Combo ${combo.name} adicionado`);
  };

  // Ao trocar a tabela de preço, reprecifica os itens já no carrinho
  useEffect(() => {
    setCart(prev => prev.map(c => {
      if (c.is_combo) {
        const cb = combos.find(x => x.id === c.combo_id);
        const pr = cb && comboPricing(cb);
        return pr ? { ...c, unit_price: pr.unit_price, components: pr.components } : c;
      }
      const p = (products || []).find(x => x.id === c.product_id);
      const price = p ? priceOf(p) : 0;
      return price > 0 ? { ...c, unit_price: price } : c;
    }));
  }, [priceType]);

  // Abre modal para digitar a quantidade antes de adicionar ao carrinho
  const openQtyPrompt = (prod) => {
    if (!prod) return;
    if ((prod.quantity || 0) <= 0) { toast.error(`${prod.name} sem estoque`); return; }
    const price = priceOf(prod);
    if (price <= 0) { toast.error(`Produto ${prod.name} não tem preço ${priceType} definido`); return; }
    setQtyDlg({ open: true, product: prod, qty: "1" });
    setTimeout(() => { qtyInputRef.current?.focus(); qtyInputRef.current?.select(); }, 100);
  };
  const confirmQtyAdd = () => {
    const prod = qtyDlg.product;
    const q = parseFloat((qtyDlg.qty || "").toString().replace(",", "."));
    if (!q || q <= 0) { toast.error("Informe uma quantidade válida"); return; }
    addProduct(prod, q);
    setQtyDlg({ open: false, product: null, qty: "1" });
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  const changeQty = (idx, delta) => {
    setCart(prev => {
      const copy = [...prev];
      const next = copy[idx].quantity + delta;
      if (next <= 0) { copy.splice(idx, 1); return copy; }
      if (next > copy[idx].max) { toast.warning(`Estoque limitado: ${copy[idx].max}`); return prev; }
      copy[idx] = { ...copy[idx], quantity: next };
      return copy;
    });
  };

  const removeItem = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.quantity * c.unit_price, 0), [cart]);
  const total = Math.max(subtotal - Number(discount || 0), 0) + Math.max(Number(freight || 0), 0);
  const change = payMethod === "dinheiro" ? Math.max(Number(amountReceived || 0) - total, 0) : 0;
  const pixChange = payMethod === "pix" && pixReceived ? Math.max(Number(pixReceived || 0) - total, 0) : 0;

  // Upload do comprovante PIX (antes de finalizar a venda, enviamos em um sale "temp" via file API? Simplicidade: usamos endpoint depois da venda)
  const uploadPixAttachment = async (file, saleId) => {
    if (!file) return null;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${API}/companies/${companyId}/factory/sales/${saleId}/pix-attachment`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.pix_attachment_url;
    } catch (e) {
      toast.error("Falha ao enviar comprovante PIX");
      return null;
    }
  };

  // ---- Barcode handler ----
  const onBarcodeSubmit = (e) => {
    e.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;
    const stripped = code.replace(/^0+/, "");
    const found = (products || []).find(p =>
      (p.barcode || "").toLowerCase() === code.toLowerCase() ||
      (p.name || "").toLowerCase() === code.toLowerCase() ||
      (p.code || "") === code ||
      (p.code || "").replace(/^0+/, "") === stripped
    );
    if (!found) { toast.error(`Produto não encontrado: ${code}`); setBarcodeInput(""); return; }
    addProduct(found, 1);
    setBarcodeInput("");
    setTimeout(() => barcodeRef.current?.focus(), 50);
  };

  // ---- Session actions ----
  const openCash = async () => {
    try {
      const { data } = await axios.post(`${API}/companies/${companyId}/factory/cash-sessions/open`, {
        opening_amount: Number(openingAmount || 0),
        opening_bank_account_id: openingBankId || null,
      });
      setCashSession(data);
      setOpenSessionDlg(false);
      setOpeningAmount("");
      setOpeningBankId("");
      toast.success(openingBankId ? "Caixa aberto · saldo debitado da conta" : "Caixa aberto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao abrir caixa");
    }
  };

  const toggleFavorite = async (client_id) => {
    try {
      await axios.post(`${API}/companies/${companyId}/factory/favorite-clients/toggle`, { client_id });
      await loadFavorites();
    } catch (e) {
      toast.error("Erro ao favoritar cliente");
    }
  };

  // ---- Validação antes de abrir painel de pagamento ----
  const canOpenPayment = cart.length > 0 && !submitting && !!cashSession;

  // ---- Finalizar venda ----
  const confirmSale = async () => {
    if (cart.length === 0) { toast.error("Carrinho vazio"); return; }
    if (payMethod === "aprazo") {
      if (!clientId) { toast.error("Venda a prazo exige cliente cadastrado"); return; }
      const credit = (clientsCredit || []).find(c => c.id === clientId);
      if (!credit || credit.credit_status !== "approved") { toast.error("Cliente não tem crédito aprovado"); return; }
      if (aprazoCount > (credit.max_installments || 1)) {
        toast.error(`Máximo de parcelas para este cliente: ${credit.max_installments}x`); return;
      }
    }
    // PIX a Prazo: exige cliente com crédito aprovado; não requer banco
    if (payMethod === "pix" && isPixAprazo) {
      if (!clientId) { toast.error("PIX a Prazo exige cliente cadastrado"); return; }
      const credit = (clientsCredit || []).find(c => c.id === clientId);
      if (!credit || credit.credit_status !== "approved") { toast.error("Cliente não tem crédito aprovado"); return; }
    }
    if (payMethod === "dinheiro" && Number(amountReceived || 0) < total) {
      toast.error("Valor recebido menor que o total"); return;
    }
    if (["pix", "cartao_debito", "cartao_credito"].includes(payMethod) && !bankAccountId && !(payMethod === "pix" && isPixAprazo)) {
      toast.error("Selecione a conta bancária que recebe o pagamento"); return;
    }
    if (withDelivery && (!delivery?.scheduled_date || !(delivery?.address || "").trim())) {
      toast.error("Informe data e endereço da entrega"); setDeliveryDlg(true); return;
    }

    setSubmitting(true);
    try {
      const body = {
        items: cart.flatMap(c => c.is_combo
          ? c.components.map(k => ({ product_id: k.product_id, quantity: k.qty * c.quantity, price_type: priceType, unit_price: k.unit_price, combo_id: c.combo_id, combo_name: c.name, combo_qty: c.quantity }))
          : [{ product_id: c.product_id, quantity: c.quantity, price_type: priceType, unit_price: c.unit_price }]),
        client_id: clientId || null,
        client_name_manual: clientNameManual || null,
        discount: Number(discount || 0),
        freight: Math.max(Number(freight || 0), 0),
        payment_method: payMethod,
        installments_count: payMethod === "aprazo" ? Number(aprazoCount || 1) : 1,
        first_due_date: payMethod === "aprazo" ? firstDue : null,
        seller_id: sellerId || null,
        seller_name_manual: sellerManual || null,
        credit_card_installments: payMethod === "cartao_credito" ? Number(cardInstallments || 1) : 1,
        amount_received: payMethod === "dinheiro" ? Number(amountReceived || 0) : null,
        pix_amount_received: payMethod === "pix" && pixReceived ? Number(pixReceived) : null,
        pix_attachment_url: payMethod === "pix" ? (pixAttachmentUrl || null) : null,
        bank_account_id: ["pix", "cartao_debito", "cartao_credito"].includes(payMethod) && !(payMethod === "pix" && isPixAprazo) ? bankAccountId : null,
        is_pix_aprazo: payMethod === "pix" && isPixAprazo,
        source: "pdv",
        notes: null,
        delivery: withDelivery ? delivery : null,
      };
      const { data } = await axios.post(`${API}/companies/${companyId}/factory/sales`, body);
      toast.success(`Venda #${String(data.sale_number).padStart(4, "0")} registrada`);
      setLastSaleId(data.sale_id);

      // Upload do comprovante PIX se o operador selecionou um arquivo (não URL ainda)
      let finalPixUrl = pixAttachmentUrl;
      if (payMethod === "pix" && pixFileRef.current?.files?.[0]) {
        const url = await uploadPixAttachment(pixFileRef.current.files[0], data.sale_id);
        if (url) finalPixUrl = url;
      }

      const sellerName = sellerId ? (employees || []).find(e => e.id === sellerId)?.name : sellerManual;
      const clientName = clientId ? (clients || []).find(c => c.id === clientId)?.name : (clientNameManual || "Consumidor");
      // Montagem do recibo
      const recData = {
        sale_id: data.sale_id,
        sale_number: data.sale_number,
        total: data.total,
        subtotal,
        discount: Number(discount || 0),
        freight: Math.max(Number(freight || 0), 0),
        delivery: withDelivery && delivery ? `${fmtYmd(delivery.scheduled_date)} · ${delivery.address}` : null,
        items: cart.map(c => c.is_combo
          ? { ...c, components: c.components.map(k => `${k.qty * c.quantity} ${k.unit} ${k.name}`) }
          : { ...c }),
        payment_method: payMethod,
        is_pix_aprazo: payMethod === "pix" && isPixAprazo,
        amount_received: payMethod === "dinheiro" ? Number(amountReceived || 0) : null,
        pix_received: payMethod === "pix" && pixReceived ? Number(pixReceived) : null,
        pix_change: pixChange,
        pix_attachment_url: finalPixUrl || null,
        change,
        card_installments: payMethod === "cartao_credito" ? Number(cardInstallments || 1) : 1,
        aprazo_count: payMethod === "aprazo" ? Number(aprazoCount || 1) : 1,
        first_due_date: payMethod === "aprazo" ? firstDue : null,
        price_type: priceType,
        seller: sellerName || "—",
        client: clientName || "Consumidor",
        company_name: companyName || "",
        date: new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }),
        nfce_status: null,
        nfce_chave: null,
      };
      // Se solicitou NFC-e, dispara emissão
      if (emitNfce) {
        try {
          toast.info("Emitindo NFC-e...");
          const cpfCnpj = nfceCpfCnpj.replace(/\D/g, "");
          const nfPayload = {};
          if (cpfCnpj.length === 11) nfPayload.cpf_destinatario = cpfCnpj;
          else if (cpfCnpj.length === 14) nfPayload.cnpj_destinatario = cpfCnpj;
          if (clientName) nfPayload.nome_destinatario = clientName;
          const { data: nfRes } = await axios.post(`${API}/companies/${companyId}/factory/sales/${data.sale_id}/nfce`, nfPayload);
          recData.nfce_status = nfRes.status;
          recData.nfce_chave = nfRes.chave_nfe || null;
          recData.nfce_ref = nfRes.ref || null;
          if (nfRes.status === "autorizado") toast.success(`NFC-e autorizada`);
          else toast.warning(`NFC-e: ${nfRes.status}`);
        } catch (e) {
          const detail = e.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : (detail?.mensagem_sefaz || detail?.mensagem || "Erro na emissão da NFC-e");
          recData.nfce_status = "erro";
          recData.nfce_error = msg;
          toast.error("NFC-e: " + msg);
        }
      }
      setReceipt(recData);
      setPaymentOpen(false);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar venda");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Nova Venda (reset) ----
  const newSale = () => {
    setCart([]);
    setDiscount("");
    setFreight("");
    setWithDelivery(false);
    setDelivery(null);
    setAmountReceived("");
    setClientId("");
    setClientNameManual("");
    setPayMethod("dinheiro");
    setCardInstallments(1);
    setAprazoCount(1);
    setIsPixAprazo(false);
    setReceipt(null);
    setSearch("");
    setBarcodeInput("");
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  // ---- Imprimir recibo (janela nova construída via DOM API, sem document.write) ----
  const printReceipt = () => {
    if (!receiptRef.current) return;
    if (!printReceiptNode(receiptRef.current, `Recibo #${receipt?.sale_number || ""}`)) toast.error("Habilite popups para imprimir");
  };

  // ---- Impressão automática ao abrir o recibo ----
  useEffect(() => {
    if (receipt && receipt._autoPrintRequested !== false) {
      // dispara impressão automática 500ms depois para dar tempo do dialog renderizar
      const t = setTimeout(() => { try { printReceipt(); } catch (e) { /* ignore */ } }, 500);
      return () => clearTimeout(t);
    }
  }, [receipt?.sale_number]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="!max-w-none !w-[98vw] !h-[96vh] p-0 overflow-hidden sm:rounded-md" data-testid="pos-dialog">
        {/* Header - compacto, uma linha */}
        <div className="bg-emerald-700 text-white px-3 py-2 flex items-center gap-2 flex-nowrap">
          <ShoppingCart className="h-5 w-5 shrink-0" />
          <h2 className="text-base font-bold whitespace-nowrap">Frente de Caixa</h2>
          {cashSession ? (
            <Badge className="bg-emerald-900 whitespace-nowrap text-xs" data-testid="pos-session-open">
              <LockOpen className="h-3 w-3 mr-1" /> Aberto · {fmt(cashSession.opening_amount || 0)}
            </Badge>
          ) : (
            <Badge className="bg-red-700 whitespace-nowrap text-xs"><Lock className="h-3 w-3 mr-1" /> Fechado</Badge>
          )}
          <div className="ml-auto flex gap-1 items-center flex-nowrap">
            <span className="text-xs opacity-90 whitespace-nowrap">Preço:</span>
            <Select value={priceType} onValueChange={setPriceType}>
              <SelectTrigger className="w-28 h-8 text-black bg-white text-sm" data-testid="pos-price-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRICE_TYPES.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs opacity-90 whitespace-nowrap ml-1">Vendedor:</span>
            <Select value={sellerId || "__none__"} onValueChange={(v) => { setSellerId(v === "__none__" ? "" : v); setSellerManual(""); }}>
              <SelectTrigger className="w-36 h-8 text-black bg-white text-sm" data-testid="pos-seller"><SelectValue placeholder="Nenhum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nenhum —</SelectItem>
                {(employees || []).filter(e => e.status !== "inativo").map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="text-white hover:bg-emerald-800 h-8 w-8 p-0" onClick={onClose} data-testid="pos-close"><X className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-12 gap-0 h-[calc(96vh-48px)]">
          {/* Produtos (esquerda) */}
          <div className="col-span-7 border-r flex flex-col h-full">
            <div className="p-2 border-b bg-gray-50 flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-2 top-3 text-gray-500" />
                <Input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} className="pl-8" placeholder="Buscar produto por nome ou código..." data-testid="pos-search" />
              </div>
              <form onSubmit={onBarcodeSubmit} className="flex gap-1">
                <div className="relative">
                  <ScanLine className="h-4 w-4 absolute left-2 top-3 text-gray-500" />
                  <Input ref={barcodeRef} value={barcodeInput} onChange={e => setBarcodeInput(e.target.value)} className="pl-8 w-56" placeholder="Código de barras (bipar/enter)" data-testid="pos-barcode" />
                </div>
                <Button type="submit" variant="outline" size="sm">Adicionar</Button>
              </form>
            </div>
            <div className="px-2 pt-2 flex gap-1" data-testid="pos-left-view-toggle">
              <Button size="sm" variant={leftView === "produtos" ? "default" : "outline"} onClick={() => setLeftView("produtos")} data-testid="pos-view-products">Produtos</Button>
              <Button size="sm" variant={leftView === "combos" ? "default" : "outline"} onClick={() => setLeftView("combos")} data-testid="pos-view-combos">
                <Gift className="h-4 w-4 mr-1" />Combos ({combos.length})
              </Button>
            </div>
            {leftView === "combos" ? (
            <div className="flex-1 overflow-y-auto p-2 grid grid-cols-3 gap-2 content-start" data-testid="pos-combos-grid">
              {combos.map(cb => {
                const pr = comboPricing(cb);
                const disabled = !pr || pr.max <= 0;
                return (
                  <button key={cb.id} onClick={() => addCombo(cb)} disabled={disabled} data-testid={`pos-combo-${cb.id}`}
                    className="text-left border rounded-lg p-2 transition hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed bg-white">
                    <div className="flex items-start justify-between gap-1">
                      <div className="text-xs font-bold truncate flex-1">{cb.name}</div>
                      <Badge className="text-[9px] px-1 py-0 bg-amber-500 shrink-0">{cb.discount_type === "percent" ? `-${cb.discount_value}%` : "FIXO"}</Badge>
                    </div>
                    <div className="text-[10px] text-gray-500 truncate">{cb.items.map(i => `${i.quantity} ${i.product_name}`).join(" + ")}</div>
                    {pr && pr.base > pr.unit_price && <div className="text-[10px] text-gray-400 line-through">{fmt(pr.base)}</div>}
                    <div className="text-emerald-700 font-bold text-sm">{pr ? fmt(pr.unit_price) : "Sem preço"}</div>
                    {pr && pr.max <= 0 && <Badge variant="destructive" className="mt-1 text-[9px]">SEM ESTOQUE</Badge>}
                  </button>
                );
              })}
              {combos.length === 0 && <div className="col-span-3 text-center text-sm text-gray-500 py-8">Nenhum combo ativo. Cadastre na aba Combos da Fábrica.</div>}
            </div>
            ) : (
            <div className="flex-1 overflow-y-auto p-2 grid grid-cols-3 gap-2 content-start">
              {availableProducts.map(p => {
                const price = priceOf(p);
                const noStock = (p.quantity || 0) <= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => openQtyPrompt(p)}
                    disabled={noStock || price <= 0}
                    data-testid={`pos-product-${p.id}`}
                    className={`text-left border rounded-lg p-2 transition hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed ${noStock ? "bg-gray-100" : "bg-white"}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="text-xs font-bold truncate flex-1">{p.name}</div>
                      {p.code && <Badge variant="outline" className="text-[9px] px-1 py-0 font-mono shrink-0">{p.code}</Badge>}
                    </div>
                    <div className="text-[10px] text-gray-500">{p.unit} · estoque {p.quantity}</div>
                    <div className="mt-1 text-emerald-700 font-bold text-sm">{fmt(price)}</div>
                    {p.barcode && <div className="text-[10px] text-gray-400 truncate">cód: {p.barcode}</div>}
                    {noStock && <Badge variant="destructive" className="mt-1 text-[9px]">SEM ESTOQUE</Badge>}
                  </button>
                );
              })}
              {availableProducts.length === 0 && (
                <div className="col-span-3 text-center text-sm text-gray-500 py-8">Nenhum produto encontrado</div>
              )}
            </div>
            )}
          </div>

          {/* Carrinho (direita) */}
          <div className="col-span-5 flex flex-col h-full bg-slate-50">
            <div className="p-2 border-b bg-white flex items-center justify-between">
              <h3 className="font-bold text-sm">Carrinho ({cart.length})</h3>
              {cart.length > 0 && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setCart([])} data-testid="pos-clear-cart"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {cart.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-8">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  Carrinho vazio
                </div>
              ) : cart.map((c, idx) => (
                <div key={`${c.product_id}-${idx}`} className="bg-white border rounded p-2" data-testid={`pos-cart-item-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">
                        {c.is_combo && <Badge className="bg-amber-500 text-[9px] px-1 py-0 mr-1">COMBO</Badge>}{c.name}
                      </div>
                      <div className="text-xs text-gray-500">{fmt(c.unit_price)} / {c.unit}</div>
                      {c.is_combo && <div className="text-[10px] text-gray-500 truncate">{c.components.map(k => `${k.qty} ${k.name}`).join(" + ")}</div>}
                    </div>
                    <button className="text-red-500 ml-1" onClick={() => removeItem(idx)} data-testid={`pos-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(idx, -1)}><Minus className="h-3 w-3" /></Button>
                      <Input className="w-14 text-center h-7" value={c.quantity}
                        onChange={e => {
                          const raw = parseFloat(e.target.value) || 0;
                          const v = c.is_combo ? Math.floor(raw) : raw;
                          setCart(prev => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], quantity: Math.min(Math.max(v, 0), copy[idx].max) };
                            return copy.filter(x => x.quantity > 0);
                          });
                        }} />
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(idx, 1)}><Plus className="h-3 w-3" /></Button>
                    </div>
                    <div className="text-sm font-bold text-emerald-700">{fmt(c.quantity * c.unit_price)}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* Totais */}
            <div className="border-t bg-white p-3 space-y-2">
              {/* Cliente */}
              <div className="flex items-center justify-between gap-2 text-sm bg-slate-100 border rounded p-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold text-gray-500 uppercase">Cliente</div>
                  <div className="truncate font-bold" data-testid="pos-selected-client">
                    {clientId
                      ? ((clients || []).find(c => c.id === clientId)?.name || "Cliente")
                      : (clientNameManual || "Consumidor")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setClientDlg(true)}
                  data-testid="pos-set-client-btn"
                  className="shrink-0"
                >
                  <Users className="h-4 w-4 mr-1" />
                  {clientId || clientNameManual ? "Trocar" : "Inserir"}
                </Button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span>Subtotal</span><span className="font-bold">{fmt(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>Desconto</span>
                <Input type="number" step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} className="w-28 h-8 text-right" data-testid="pos-discount" />
              </div>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>Frete</span>
                <Input type="number" step="0.01" min="0" value={freight} onChange={e => setFreight(e.target.value)} className="w-28 h-8 text-right" placeholder="0,00" data-testid="pos-freight" />
              </div>
              <div className="flex items-center justify-between gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={withDelivery} data-testid="pos-with-delivery"
                    onChange={e => {
                      const on = e.target.checked;
                      setWithDelivery(on);
                      if (on) {
                        const cl = (clients || []).find(c => c.id === clientId);
                        setDelivery(d => d || { scheduled_date: addDays(todayBR(), 1), address: cl?.address || "", contact_phone: cl?.phone || "", driver_user_id: null, notes: "" });
                        setDeliveryDlg(true);
                      }
                    }} />
                  <Truck className="h-4 w-4" />Com entrega
                </label>
                {withDelivery && delivery && (
                  <button type="button" className="text-xs underline truncate max-w-[60%]" onClick={() => setDeliveryDlg(true)} data-testid="pos-delivery-summary">
                    {fmtYmd(delivery.scheduled_date)} · {delivery.address || "sem endereço"}
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between text-lg border-t pt-2">
                <span className="font-bold">TOTAL</span>
                <span className="font-bold text-emerald-700" data-testid="pos-total">{fmt(total)}</span>
              </div>
              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-base h-11"
                disabled={!canOpenPayment}
                onClick={() => setPaymentOpen(true)}
                data-testid="pos-finalize-btn"
              >
                <DollarSign className="h-5 w-5 mr-2" /> Finalizar (F2)
              </Button>
            </div>
          </div>
        </div>

        {/* ===== CLIENTE ===== */}
        <Dialog open={clientDlg} onOpenChange={setClientDlg}>
          <DialogContent className="max-w-xl" data-testid="pos-client-dialog">
            <h3 className="text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5" /> Selecionar Cliente</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold block">Nome manual (venda avulsa)</label>
                <Input
                  value={clientNameManual}
                  onChange={e => { setClientNameManual(e.target.value); if (e.target.value) setClientId(""); }}
                  placeholder="Ex: João da Padaria"
                  data-testid="pos-client-manual"
                />
              </div>
              <div className="text-center text-xs text-gray-400">OU</div>
              <div>
                <label className="text-xs font-bold block">Buscar cliente cadastrado</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-3 text-gray-500" />
                  <Input value={clientSearch} onChange={e => setClientSearch(e.target.value)} className="pl-8" placeholder="Nome ou CPF/CNPJ..." data-testid="pos-client-search" />
                </div>
              </div>
              <div className="border rounded max-h-64 overflow-y-auto">
                {(clients || [])
                  .filter(c => {
                    const q = clientSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (c.name || "").toLowerCase().includes(q) || (c.cpf_cnpj || "").toLowerCase().includes(q);
                  })
                  .slice(0, 50)
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setClientId(c.id);
                        setClientNameManual("");
                        if (c.cpf_cnpj) setNfceCpfCnpj(c.cpf_cnpj);
                        setClientDlg(false);
                        setClientSearch("");
                      }}
                      data-testid={`pos-client-pick-${c.id}`}
                      className={`block w-full text-left p-2 border-b hover:bg-emerald-50 ${clientId === c.id ? "bg-emerald-100" : ""}`}
                    >
                      <div className="font-bold text-sm">{c.name}</div>
                      {c.cpf_cnpj && <div className="text-xs text-gray-500">{c.cpf_cnpj}</div>}
                    </button>
                  ))}
                {(clients || []).length === 0 && (
                  <div className="p-4 text-center text-sm text-gray-500">Nenhum cliente cadastrado</div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" className="flex-1" onClick={() => { setClientId(""); setClientNameManual(""); setNfceCpfCnpj(""); setClientDlg(false); }} data-testid="pos-client-clear">
                Limpar (Consumidor)
              </Button>
              <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={() => setClientDlg(false)} data-testid="pos-client-ok">
                Confirmar
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        {/* ===== PROMPT DE QUANTIDADE ===== */}
        <Dialog open={qtyDlg.open} onOpenChange={(v) => { if (!v) setQtyDlg({ open: false, product: null, qty: "1" }); }}>
          <DialogContent className="max-w-sm" data-testid="pos-qty-dialog">
            {qtyDlg.product && (
              <div className="space-y-3">
                <div>
                  <div className="text-base font-bold">{qtyDlg.product.name}</div>
                  <div className="text-xs text-gray-500">
                    {fmt(priceOf(qtyDlg.product))} / {qtyDlg.product.unit || "un"} · estoque {qtyDlg.product.quantity}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold block">Quantidade</label>
                  <Input
                    ref={qtyInputRef}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={qtyDlg.qty}
                    onChange={e => setQtyDlg({ ...qtyDlg, qty: e.target.value })}
                    onKeyDown={e => { if (e.key === "Enter") confirmQtyAdd(); }}
                    className="text-xl text-center h-12"
                    data-testid="pos-qty-input"
                  />
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[1, 2, 5, 10].map(n => (
                    <Button key={n} size="sm" variant="outline" onClick={() => setQtyDlg({ ...qtyDlg, qty: String(n) })}>
                      {n}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-xs text-gray-500">Subtotal:</span>
                  <span className="text-lg font-bold text-emerald-700">
                    {fmt((parseFloat(qtyDlg.qty) || 0) * priceOf(qtyDlg.product))}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setQtyDlg({ open: false, product: null, qty: "1" })}>Cancelar</Button>
                  <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={confirmQtyAdd} data-testid="pos-qty-confirm">
                    Adicionar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== PAINEL DE PAGAMENTO ===== */}
        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pos-payment-dialog">
            <h3 className="text-lg font-bold">Pagamento · Total {fmt(total)}</h3>

            {/* Favoritos */}
            {favorites.length > 0 && (
              <div className="border rounded p-2 bg-amber-50">
                <div className="text-xs font-bold mb-1 flex items-center gap-1"><Star className="h-3 w-3 text-amber-600" /> Clientes Favoritos / Frequentes</div>
                <div className="flex flex-wrap gap-1">
                  {favorites.slice(0, 10).map(f => (
                    <button
                      key={f.client_id}
                      onClick={() => { setClientId(f.client_id); setClientNameManual(""); if (f.cpf_cnpj) setNfceCpfCnpj(f.cpf_cnpj); }}
                      className={`text-xs px-2 py-1 rounded border transition ${clientId === f.client_id ? "bg-emerald-600 text-white border-emerald-700" : "bg-white hover:bg-emerald-50"}`}
                      data-testid={`pos-fav-${f.client_id}`}
                    >
                      {f.is_favorite && <Star className="h-3 w-3 inline mr-1 text-amber-500 fill-amber-500" />}
                      {f.name} {f.count > 0 && <span className="opacity-60">({f.count})</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Tabs value={payMethod} onValueChange={setPayMethod} className="mt-2">
              <TabsList className="grid grid-cols-5 w-full">
                <TabsTrigger value="dinheiro" data-testid="pay-tab-dinheiro"><DollarSign className="h-4 w-4 mr-1" />Dinheiro</TabsTrigger>
                <TabsTrigger value="pix" data-testid="pay-tab-pix"><Smartphone className="h-4 w-4 mr-1" />PIX</TabsTrigger>
                <TabsTrigger value="cartao_debito" data-testid="pay-tab-debito"><CreditCard className="h-4 w-4 mr-1" />Débito</TabsTrigger>
                <TabsTrigger value="cartao_credito" data-testid="pay-tab-credito"><CreditCard className="h-4 w-4 mr-1" />Crédito</TabsTrigger>
                <TabsTrigger value="aprazo" data-testid="pay-tab-aprazo"><Clock className="h-4 w-4 mr-1" />A Prazo</TabsTrigger>
              </TabsList>

              <TabsContent value="dinheiro" className="space-y-2 pt-3">
                <label className="text-xs font-bold block">Valor Recebido</label>
                <Input type="number" step="0.01" value={amountReceived} onChange={e => setAmountReceived(e.target.value)} placeholder={fmt(total)} data-testid="pos-amount-received" className="text-lg" />
                <div className="flex gap-1 flex-wrap">
                  {[total, 50, 100, 200].map(v => (
                    <Button key={v} size="sm" variant="outline" onClick={() => setAmountReceived(String(v))}>{fmt(v)}</Button>
                  ))}
                </div>
                <div className="border rounded p-2 bg-amber-50 flex items-center justify-between mt-2">
                  <span className="font-bold">TROCO</span>
                  <span className="text-xl font-bold text-amber-800" data-testid="pos-change">{fmt(change)}</span>
                </div>
              </TabsContent>

              <TabsContent value="pix" className="space-y-2 pt-3">
                <label className="flex items-start gap-2 cursor-pointer bg-sky-50 border border-sky-300 rounded p-2">
                  <input type="checkbox" checked={isPixAprazo} onChange={e => setIsPixAprazo(e.target.checked)} data-testid="pos-pix-aprazo-toggle" className="mt-1" />
                  <span className="text-xs">
                    <b>PIX a Prazo (4 dias)</b> — gera 1 parcela com vencimento em 4 dias. Exige cliente com <b>crédito aprovado</b>.
                  </span>
                </label>
                {isPixAprazo ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-bold block">Cliente (com crédito aprovado) *</label>
                      <Select value={clientId || "__none__"} onValueChange={v => setClientId(v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="pos-pix-aprazo-client"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Selecione —</SelectItem>
                          {(clientsCredit || []).filter(c => c.credit_status === "approved").map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name} (até {c.max_installments}x · {c.max_days}d)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="border rounded p-2 bg-sky-50 text-sm">
                      <div className="flex items-center justify-between">
                        <span>Vencimento:</span>
                        <b>{(() => { const d = new Date(); d.setDate(d.getDate() + 4); return d.toLocaleDateString("pt-BR"); })()}</b>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Valor da parcela:</span>
                        <b>{fmt(total)}</b>
                      </div>
                    </div>
                  </div>
                ) : (
                <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-bold block">Valor recebido via PIX</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={pixReceived}
                      onChange={e => setPixReceived(e.target.value)}
                      placeholder={fmt(total)}
                      data-testid="pos-pix-received"
                    />
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {[total, Math.ceil(total / 10) * 10, Math.ceil(total / 50) * 50, Math.ceil(total / 100) * 100]
                        .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
                        .map(v => <Button key={v} size="sm" variant="outline" onClick={() => setPixReceived(String(v))}>{fmt(v)}</Button>)}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold block">Troco em dinheiro</label>
                    <div className={`border rounded p-2 h-10 flex items-center justify-end font-bold text-lg ${pixChange > 0 ? "bg-amber-50 text-amber-800" : "bg-gray-50 text-gray-400"}`} data-testid="pos-pix-change">
                      {fmt(pixChange)}
                    </div>
                    {pixChange > 0 && <div className="text-[11px] text-amber-700 mt-1">Entregar {fmt(pixChange)} do caixa físico</div>}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold block">Conta bancária que recebe *</label>
                  <Select value={bankAccountId || "__none__"} onValueChange={v => setBankAccountId(v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="pos-pay-bank-pix"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold block">Comprovante PIX (opcional)</label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={pixFileRef}
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        setPixAttachmentName(f?.name || "");
                        if (f) setPixAttachmentUrl("pending");
                      }}
                      data-testid="pos-pix-file"
                    />
                    <Button type="button" variant="outline" onClick={() => pixFileRef.current?.click()} data-testid="pos-pix-attach-btn">
                      <FileText className="h-4 w-4 mr-1" /> Anexar
                    </Button>
                    {pixAttachmentName && <span className="text-xs truncate">{pixAttachmentName}</span>}
                    {pixAttachmentName && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => { if (pixFileRef.current) pixFileRef.current.value = ""; setPixAttachmentUrl(""); setPixAttachmentName(""); }}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">Imagem ou PDF do comprovante. Enviado automaticamente ao confirmar a venda.</div>
                </div>
                </>
                )}
              </TabsContent>

              <TabsContent value="cartao_debito" className="space-y-2 pt-3">
                <p className="text-sm">Passe no cartão de débito antes de finalizar.</p>
                <div className="border rounded p-3 bg-indigo-50 text-center">
                  <CreditCard className="h-6 w-6 mx-auto mb-1 text-indigo-700" />
                  <p className="font-bold text-lg">{fmt(total)}</p>
                </div>
                <div>
                  <label className="text-xs font-bold block">Conta bancária que recebe *</label>
                  <Select value={bankAccountId || "__none__"} onValueChange={v => setBankAccountId(v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="pos-pay-bank-debito"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="cartao_credito" className="space-y-2 pt-3">
                <label className="text-xs font-bold block">Parcelas no cartão</label>
                <Select value={String(cardInstallments)} onValueChange={(v) => setCardInstallments(Number(v))}>
                  <SelectTrigger data-testid="pos-card-installments"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                      <SelectItem key={n} value={String(n)}>{n}x de {fmt(total / n)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-gray-500">O parcelamento é cobrado pela operadora do cartão; o valor total é creditado como venda à vista no sistema.</div>
                <div>
                  <label className="text-xs font-bold block">Conta bancária que recebe *</label>
                  <Select value={bankAccountId || "__none__"} onValueChange={v => setBankAccountId(v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="pos-pay-bank-credito"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="aprazo" className="space-y-2 pt-3">
                <label className="text-xs font-bold block">Cliente *</label>
                <Select value={clientId || "__none__"} onValueChange={(v) => setClientId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="pos-aprazo-client"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Selecione —</SelectItem>
                    {/* Se cliente já está selecionado mas não aparece na lista aprovada, mostra ele no topo */}
                    {clientId && !(clientsCredit || []).some(c => c.id === clientId && c.credit_status === "approved") && (() => {
                      const c = (clients || []).find(x => x.id === clientId);
                      return c ? <SelectItem key={c.id} value={c.id}>{c.name} (⚠ sem crédito aprovado)</SelectItem> : null;
                    })()}
                    {(clientsCredit || []).filter(c => c.credit_status === "approved").map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} (até {c.max_installments}x · {c.max_days}d)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {clientId && !(clientsCredit || []).some(c => c.id === clientId && c.credit_status === "approved") && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1">
                    Este cliente ainda não tem crédito aprovado. Aprove em "Clientes / Crédito" antes de vender a prazo.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-bold block">Parcelas</label>
                    <Input type="number" min={1} value={aprazoCount} onChange={e => setAprazoCount(Number(e.target.value) || 1)} data-testid="pos-aprazo-count" />
                  </div>
                  <div>
                    <label className="text-xs font-bold block">1ª Parcela</label>
                    <Input type="date" value={firstDue} onChange={e => setFirstDue(e.target.value)} data-testid="pos-aprazo-first-due" />
                  </div>
                </div>
                <div className="text-xs text-gray-500">Cada parcela: {fmt(total / Math.max(aprazoCount, 1))}</div>
              </TabsContent>
            </Tabs>

            {/* Cliente opcional para métodos à vista */}
            {payMethod !== "aprazo" && (
              <div className="border-t pt-2 mt-3">
                <label className="text-xs font-bold block">Cliente (opcional)</label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={clientId || "__none__"} onValueChange={(v) => setClientId(v === "__none__" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Consumidor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Consumidor —</SelectItem>
                      {(clients || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Ou nome manual" value={clientNameManual} onChange={e => setClientNameManual(e.target.value)} />
                </div>
              </div>
            )}

            {/* NFC-e opcional */}
            <div className="border-t pt-2 mt-3 bg-sky-50 p-2 rounded">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emitNfce}
                  onChange={e => setEmitNfce(e.target.checked)}
                  data-testid="pos-emit-nfce"
                  className="h-4 w-4"
                />
                <FileText className="h-4 w-4 text-sky-700" />
                <span className="font-bold text-sm">Emitir NFC-e (nota fiscal de consumidor)</span>
              </label>
              {emitNfce && (
                <div className="mt-2">
                  <label className="text-xs font-bold block">CPF ou CNPJ do consumidor (opcional)</label>
                  <Input value={nfceCpfCnpj} onChange={e => setNfceCpfCnpj(e.target.value)} placeholder="Ex: 123.456.789-00" data-testid="pos-nfce-cpf" />
                  <div className="text-[11px] text-gray-600 mt-1">Deixe em branco para emitir sem identificar o consumidor. A nota é emitida via Focus NFe após a venda ser salva.</div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setPaymentOpen(false)} className="flex-1">Cancelar</Button>
              <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={confirmSale} disabled={submitting} data-testid="pos-confirm-sale">
                {submitting ? "Processando..." : "Confirmar Venda"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== RECIBO ===== */}
        <Dialog open={!!receipt} onOpenChange={(v) => { if (!v) newSale(); }}>
          <DialogContent className="max-w-md" data-testid="pos-receipt-dialog">
            <div ref={receiptRef}>
              <SaleReceiptContent receipt={receipt} />
            </div>

            {receipt?.nfce_status === "autorizado" && (
              <Button
                variant="outline"
                className="w-full border-sky-600 text-sky-700"
                onClick={() => window.open(`${API}/companies/${companyId}/factory/sales/${receipt.sale_id}/nfce/danfce`, "_blank")}
                data-testid="pos-open-danfce"
              >
                <FileText className="h-4 w-4 mr-1" /> Abrir DANFCe (PDF)
              </Button>
            )}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" className="flex-1" onClick={printReceipt} data-testid="pos-print-receipt"><Printer className="h-4 w-4 mr-1" />Imprimir</Button>
              <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={newSale} data-testid="pos-new-sale"><Receipt className="h-4 w-4 mr-1" />Nova Venda</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== ABERTURA DE CAIXA ===== */}
        <Dialog open={openSessionDlg} onOpenChange={(v) => { if (!v && !cashSession) onClose(); setOpenSessionDlg(v); }}>
          <DialogContent className="max-w-md" data-testid="pos-open-session-dialog">
            <h3 className="text-lg font-bold flex items-center gap-2"><Lock className="h-5 w-5 text-amber-600" /> Abertura de Caixa</h3>
            <p className="text-sm text-gray-700">Para começar a vender, confirme o valor em dinheiro que está no caixa agora.</p>
            <div>
              <label className="text-xs font-bold block">Valor de Abertura (dinheiro inicial)</label>
              <Input type="number" step="0.01" value={openingAmount} onChange={e => setOpeningAmount(e.target.value)} placeholder="0,00" autoFocus data-testid="pos-opening-amount" />
            </div>
            <div>
              <label className="text-xs font-bold block">Debitar de qual conta? (opcional)</label>
              <Select value={openingBankId || "__none__"} onValueChange={v => setOpeningBankId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="pos-opening-bank"><SelectValue placeholder="Nenhuma (só registrar o valor)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhuma (apenas registrar) —</SelectItem>
                  {(bankAccounts || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name} · {Number(b.current_balance || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-gray-500 mt-1">Se escolher uma conta, o valor é debitado dela e lançado como <b>Suprimento inicial</b> em Transações.</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" className="flex-1" onClick={() => { setOpenSessionDlg(false); onClose(); }}>Cancelar</Button>
              <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={openCash} data-testid="pos-confirm-open-session">
                <LockOpen className="h-4 w-4 mr-1" />Abrir Caixa
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dados da entrega */}
        <Dialog open={deliveryDlg} onOpenChange={(o) => {
          if (!o && (!delivery?.scheduled_date || !(delivery?.address || "").trim())) setWithDelivery(false);
          setDeliveryDlg(o);
        }}>
          <DialogContent className="max-w-lg" data-testid="pos-delivery-dialog">
            <DialogTitle className="text-lg font-bold flex items-center gap-2"><Truck className="h-5 w-5" /> Dados da Entrega</DialogTitle>
            {delivery && <DeliveryFormFields value={delivery} onChange={setDelivery} drivers={drivers} />}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" className="flex-1" onClick={() => { setWithDelivery(false); setDeliveryDlg(false); }} data-testid="pos-delivery-remove">Sem entrega</Button>
              <Button className="flex-1" onClick={() => {
                if (!delivery?.scheduled_date || !(delivery?.address || "").trim()) { toast.error("Informe data e endereço"); return; }
                setDeliveryDlg(false);
              }} data-testid="pos-delivery-save">Confirmar entrega</Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
